import { PRODUCT_ENTITLEMENTS } from '@headline/schemas'
import { useSuspenseQuery } from '@tanstack/react-query'
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router'
import { LibraryView } from '@/components/stream/LibraryView'
import { accountQuery, subaccountsQuery } from '@/lib/queries'
import { streamSearchSchema } from '@/lib/stream-filters'
import { libraryViewsQuery, streamConfigQuery, streamHitsQuery } from '@/lib/stream-queries'
import { primarySubaccount } from '@/lib/stream-workspace'

/**
 * The account workspace's Library — the row-level view of its stream (the primary sub-account's,
 * which may attach client topics; hence the Client column). Gated on `delivery:library` like every
 * product: not bought means no surface, redirect to Home. Search params are the Analytics
 * drill-through contract.
 */
export const Route = createFileRoute('/app/accounts/$accountId_/library')({
  validateSearch: streamSearchSchema,
  loader: async ({ context, params }) => {
    const account = await context.queryClient.ensureQueryData(accountQuery(params.accountId))
    if (!account.entitlements.includes(PRODUCT_ENTITLEMENTS.library)) {
      throw redirect({ to: '/app/accounts/$accountId', params: { accountId: params.accountId } })
    }
    const subs = await context.queryClient.ensureQueryData(subaccountsQuery(params.accountId))
    const primary = primarySubaccount(subs.subaccounts)
    // The rows are started here and NOT awaited: `prefetchQuery` settles only once the response
    // has arrived, so awaiting it held the route — a blank page — for the whole read (CLI-758).
    // The page paints its chrome at once and the table fills in when the rows land.
    void context.queryClient.prefetchQuery(streamHitsQuery(primary.id))
    await Promise.all([
      context.queryClient.ensureQueryData(streamConfigQuery(primary.id)),
      context.queryClient.ensureQueryData(libraryViewsQuery(primary.id)),
    ])
  },
  component: AccountLibraryPage,
})

function AccountLibraryPage() {
  const { accountId } = Route.useParams()
  const search = Route.useSearch()
  const navigate = useNavigate()
  const { data: account } = useSuspenseQuery(accountQuery(accountId))
  const { data: subs } = useSuspenseQuery(subaccountsQuery(accountId))

  return (
    <LibraryView
      workspaceName={account.name}
      storageKey={accountId}
      subaccountId={primarySubaccount(subs.subaccounts).id}
      accountId={accountId}
      initialSearch={search}
      onConfigure={() =>
        void navigate({ to: '/app/accounts/$accountId/stream-config', params: { accountId } })
      }
      analyticsEnabled={account.entitlements.includes(PRODUCT_ENTITLEMENTS.analytics)}
      onClearDrill={() =>
        void navigate({
          to: '/app/accounts/$accountId/library',
          params: { accountId },
          search: { ...search, drill: undefined },
          replace: true,
        })
      }
    />
  )
}
